import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PhotoMosaic from '../components/PhotoMosaic';
import EditAlbumModal from '../components/EditAlbumModal';
import PinModal from '../components/PinModal';
import { useAuth } from '../contexts/AuthContext';
import { getAlbumPhotos, deletePhoto, getAlbumMetadata, setAlbumCover, setCoverCropPosition, updateAlbumDetails } from '../services/albumService';
import { getSiteConfig } from '../services/firestoreService';
import { isUploadPinCookieValid, setUploadPinCookie, clearUploadPinCookie } from '../utils/cookieUtils';
import JSZip from 'jszip';

const AlbumPage = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isAuthorized } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [albumData, setAlbumData] = useState(null);
  const [cropPosition, setCropPosition] = useState(50);
  const [showCropSlider, setShowCropSlider] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [uploadPin, setUploadPin] = useState('202526');
  const [uploadPinUpdatedAt, setUploadPinUpdatedAt] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  const loadAlbumData = async () => {
    try {
      // Check sessionStorage cache first
      const cacheKey = `album_${albumId}`;
      const cached = sessionStorage.getItem(cacheKey);
      const cacheTime = sessionStorage.getItem(`${cacheKey}_time`);
      const now = Date.now();

      // Use cache if it's less than 5 minutes old
      if (cached && cacheTime && (now - parseInt(cacheTime)) < 5 * 60 * 1000) {
        const cachedData = JSON.parse(cached);
        setPhotos(cachedData.photos);
        setAlbumData(cachedData.metadata);
        if (cachedData.metadata?.coverPhoto?.cropPosition !== undefined) {
          setCropPosition(cachedData.metadata.coverPhoto.cropPosition);
        }
        setLoading(false);
        return;
      }

      const photoList = await getAlbumPhotos(albumId);
      setPhotos(photoList);

      try {
        const metadata = await getAlbumMetadata(albumId);
        console.log('AlbumPage: Loaded metadata:', metadata);
        console.log('AlbumPage: Upload key present?', !!metadata.uploadKey);
        setAlbumData(metadata);

        // Cache the data
        sessionStorage.setItem(cacheKey, JSON.stringify({
          photos: photoList,
          metadata: metadata
        }));
        sessionStorage.setItem(`${cacheKey}_time`, now.toString());

        // Load saved crop position if available
        if (metadata.coverPhoto?.cropPosition !== undefined) {
          setCropPosition(metadata.coverPhoto.cropPosition);
        }
      } catch (metadataError) {
        console.log('No metadata file found, using default album data');
        // Set default album data if metadata doesn't exist
        const defaultData = {
          id: albumId,
          title: albumId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          date: new Date().toISOString(),
          category: ''
        };
        setAlbumData(defaultData);

        // Cache with default data
        sessionStorage.setItem(cacheKey, JSON.stringify({
          photos: photoList,
          metadata: defaultData
        }));
        sessionStorage.setItem(`${cacheKey}_time`, now.toString());
      }
    } catch (error) {
      console.error('Error loading album data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlbumData();
    loadSiteConfig();
  }, [albumId]);

  const loadSiteConfig = async () => {
    try {
      const config = await getSiteConfig();
      setUploadPin(config.uploadPin || '202526');
      setUploadPinUpdatedAt(config.uploadPinUpdatedAt);
    } catch (error) {
      console.error('Error loading site config:', error);
    }
  };

  const refreshPhotos = () => {
    // Clear cache before reloading
    const cacheKey = `album_${albumId}`;
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem(`${cacheKey}_time`);
    loadAlbumData();
  };

  const handleDeletePhoto = async (photoName) => {
    try {
      await deletePhoto(albumId, photoName);
      setPhotos(prev => prev.filter(photo => photo.name !== photoName));

      // If this was the cover photo, clear it from metadata
      if (albumData?.coverPhoto?.name === photoName) {
        const updatedMetadata = { ...albumData };
        delete updatedMetadata.coverPhoto;
        setAlbumData(updatedMetadata);
      }

      // Clear cache after deletion
      const cacheKey = `album_${albumId}`;
      sessionStorage.removeItem(cacheKey);
      sessionStorage.removeItem(`${cacheKey}_time`);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo. Please try again.');
    }
  };

  const handleSetCover = async (photo) => {
    try {
      console.log('AlbumPage: Setting cover for:', { albumId, photo });
      const updatedMetadata = await setAlbumCover(albumId, photo.url, photo.name, photo.thumbnailUrl);
      console.log('AlbumPage: Updated metadata received:', updatedMetadata);
      setAlbumData(updatedMetadata);
      console.log('AlbumPage: Album data state updated');
    } catch (error) {
      console.error('Error setting album cover:', error);
      alert('Error setting album cover. Please try again.');
    }
  };

  const handleCropPositionChange = async (newCropPosition) => {
    setCropPosition(newCropPosition);
    
    // Debounce the save operation to avoid too many API calls
    if (handleCropPositionChange.timeout) {
      clearTimeout(handleCropPositionChange.timeout);
    }
    
    handleCropPositionChange.timeout = setTimeout(async () => {
      try {
        const updatedMetadata = await setCoverCropPosition(albumId, newCropPosition);
        setAlbumData(updatedMetadata);
        console.log('Crop position saved:', newCropPosition);
      } catch (error) {
        console.error('Error saving crop position:', error);
      }
    }, 500); // Save after 500ms of no changes
  };

  const handleUpdateAlbum = async (updatedData) => {
    try {
      const updatedMetadata = await updateAlbumDetails(albumId, updatedData);
      setAlbumData(updatedMetadata);
      console.log('Album updated successfully');
    } catch (error) {
      console.error('Error updating album:', error);
      alert('Error updating album. Please try again.');
    }
  };

  const handleAddPhotosClick = async () => {
    // If authenticated, navigate directly
    if (isAuthenticated) {
      navigate(`/album/${albumId}/upload`, { state: { albumTitle: albumData?.title } });
      return;
    }

    // For unauthenticated users, check PIN cookie validity
    const isValid = await isUploadPinCookieValid(uploadPin, uploadPinUpdatedAt);
    if (isValid) {
      navigate(`/album/${albumId}/upload`, { state: { albumTitle: albumData?.title } });
    } else {
      // Clear old cookie if exists and show PIN modal
      clearUploadPinCookie();
      setShowPinModal(true);
    }
  };

  const handlePinValidated = async (pin) => {
    await setUploadPinCookie(pin, uploadPinUpdatedAt);
    navigate(`/album/${albumId}/upload`, { state: { albumTitle: albumData?.title } });
  };

  const toggleSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedPhotos(new Set());
  };

  const downloadSelectedPhotos = async () => {
    const selectedCount = selectedPhotos.size;

    if (selectedCount === 0) {
      alert('Please select at least one photo to download.');
      return;
    }

    if (selectedCount > 50) {
      alert('You can download a maximum of 50 photos at a time. Please reduce your selection.');
      return;
    }

    setDownloading(true);
    const zip = new JSZip();
    let successCount = 0;
    let failCount = 0;

    try {
      const selectedPhotosList = photos.filter(photo => selectedPhotos.has(photo.id));

      for (let i = 0; i < selectedPhotosList.length; i++) {
        const photo = selectedPhotosList[i];
        try {
          // Update progress message
          console.log(`Downloading ${i + 1}/${selectedCount}: ${photo.name}`);

          // Fetch the photo as a blob
          const response = await fetch(photo.url);
          if (!response.ok) throw new Error(`Failed to fetch ${photo.name}`);

          const blob = await response.blob();
          zip.file(photo.name, blob);
          successCount++;
        } catch (error) {
          console.error(`Error downloading ${photo.name}:`, error);
          failCount++;
        }
      }

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Create download link
      const blobUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = blobUrl;

      // Create filename from album title and date
      const albumTitle = albumData?.title || albumId;
      const dateStr = albumData?.date ? albumData.date : new Date().toISOString().split('T')[0];
      link.download = `${albumTitle.replace(/[^a-z0-9]/gi, '-')}-${dateStr}.zip`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      // Show summary
      if (failCount > 0) {
        alert(`Download complete!\nSuccessful: ${successCount}\nFailed: ${failCount}`);
      }

      // Clear selection after successful download
      clearSelection();
    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('An error occurred while creating the zip file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <div className="text-center py-12 text-text-dark text-lg">Loading photos...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Cover Photo Banner */}
      {albumData?.coverPhoto && (
        <div className="w-full relative overflow-hidden" style={{ height: 'min(66.67vw, 800px)' }}>
          <img
            src={albumData.coverPhoto.thumbnailUrl || albumData.coverPhoto.url}
            alt={`${albumData.title} cover`}
            className={`absolute inset-0 w-full h-full transition-all duration-300 ${
              albumData.coverPhoto.name === 'bear.png' ? 'object-contain p-16 opacity-40' : 'object-cover'
            }`}
            style={albumData.coverPhoto.name !== 'bear.png' ? { objectPosition: `center ${cropPosition}%` } : {}}
          />
          <div className="absolute inset-0 bg-black/30 flex items-end">
            <div className="absolute top-4 left-4">
              <button className="p-2 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-white/30" onClick={() => navigate('/')} title="Back to Albums">
                <span className="material-icons text-base">arrow_back</span>
                <span className="hidden sm:inline">Back to Albums</span>
              </button>
            </div>
            <div className="p-4 sm:p-8 text-white">
              <h1 className="m-0 text-white text-3xl sm:text-4xl font-light font-serif">{albumData?.title || albumId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h1>
              {albumData?.category && (
                <p className="m-0 text-white/90 text-base font-sans mt-1 uppercase tracking-wide">
                  {albumData.category}
                </p>
              )}
              {albumData?.date && (
                <p className="m-0 text-white/80 text-lg font-sans mt-2">
                  {new Date(albumData.date + 'T00:00:00').toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
            <div className="absolute top-4 right-4 flex flex-col gap-2">
              {isAuthorized && (
                <button className="p-2 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-white/30" onClick={() => setShowEditModal(true)} title="Edit Album">
                  <span className="material-icons text-base">edit</span>
                  <span className="hidden sm:inline">Edit Album</span>
                </button>
              )}
              <button className="p-2 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-white/30" onClick={handleAddPhotosClick} title="Add Photos">
                <span className="material-icons text-base">add_a_photo</span>
                <span className="hidden sm:inline">Add Photos</span>
              </button>
              {isAuthorized && albumData?.uploadKey && (
                <button className="p-2 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-white/30" onClick={() => {
                  const uploadUrl = `${window.location.origin}/album/${albumId}/upload/${albumData.uploadKey}`;
                  navigator.clipboard.writeText(uploadUrl).then(() => {
                    alert('Upload link copied to clipboard!');
                  });
                }} title="Copy Upload Link">
                  <span className="material-icons text-base">link</span>
                  <span className="hidden sm:inline">Copy Upload Link</span>
                </button>
              )}
            </div>
            
            {isAuthorized && (
              <div className="absolute bottom-4 right-4">
                <button 
                  className="p-2 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-white/30"
                  onClick={() => setShowCropSlider(!showCropSlider)}
                  title="Adjust Crop"
                >
                  <span className="material-icons text-base">crop</span>
                  <span className="hidden sm:inline">Adjust Crop</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Crop Position Slider */}
          {showCropSlider && (
            <div className="absolute bottom-20 right-4 w-16 h-[400px] bg-white/20 backdrop-blur-sm rounded-lg p-4 flex flex-col items-center">
              <button 
                className="text-white/80 hover:text-white text-xl leading-none mb-4"
                onClick={() => setShowCropSlider(false)}
              >
                ×
              </button>
              <div className="flex flex-col items-center gap-2 text-white flex-1">
                <span className="text-xs writing-mode-vertical-rl text-orientation-mixed">Top</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={cropPosition}
                  onChange={(e) => handleCropPositionChange(e.target.value)}
                  className="h-full w-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider vertical-slider"
                  style={{
                    writingMode: 'bt-lr',
                    WebkitAppearance: 'slider-vertical',
                    background: `linear-gradient(to top, #ffffff80 0%, #ffffff80 ${cropPosition}%, #ffffff30 ${cropPosition}%, #ffffff30 100%)`
                  }}
                />
                <span className="text-xs writing-mode-vertical-rl text-orientation-mixed">Bottom</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Header for albums without cover photo */}
      {!albumData?.coverPhoto && (
        <div className="p-4 sm:p-8">
          <header className="flex justify-between items-center mb-4 sm:mb-8 pb-4 border-b border-gray-200">
            <div className="flex items-center">
              <button className="bg-none border-none text-accent text-base cursor-pointer py-2 pr-0 mr-4 transition-colors duration-200 hover:text-accent/80 hover:underline" onClick={() => navigate('/')}>
                ← Back to Albums
              </button>
              <h1 className="m-0 text-text-dark text-3xl font-light font-serif">{albumData?.title || albumId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h1>
            </div>
            <div className="flex gap-2">
              <button className="px-6 py-3 bg-accent text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-accent/80" onClick={handleAddPhotosClick}>
                <span className="material-icons">add_a_photo</span>
                Add Photos
              </button>
              {isAuthorized && albumData?.uploadKey && (
                <button className="px-6 py-3 bg-blue-600 text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-blue-700" onClick={() => {
                  const uploadUrl = `${window.location.origin}/album/${albumId}/upload/${albumData.uploadKey}`;
                  navigator.clipboard.writeText(uploadUrl).then(() => {
                    alert('Upload link copied to clipboard!');
                  });
                }} title="Copy Upload Link">
                  <span className="material-icons">link</span>
                  Copy Upload Link
                </button>
              )}
            </div>
          </header>
        </div>
      )}

      {/* Selection Toolbar */}
      {selectedPhotos.size > 0 && (
        <div className="sticky top-0 z-50 bg-blue-600/95 backdrop-blur-sm border-b border-blue-500 py-3 px-4 sm:px-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-white font-medium">
              {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-4 py-2 bg-white text-blue-600 border-none rounded text-sm font-medium cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={downloadSelectedPhotos}
              disabled={downloading || selectedPhotos.size === 0}
            >
              <span className="material-icons text-base">download</span>
              <span>{downloading ? 'Downloading...' : `Download ${selectedPhotos.size} Photo${selectedPhotos.size !== 1 ? 's' : ''}`}</span>
            </button>
            <button
              className="px-4 py-2 bg-white/20 text-white border border-white/30 rounded text-sm font-medium cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-white/30"
              onClick={clearSelection}
            >
              <span className="material-icons text-base">close</span>
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      <div className="px-2 sm:px-4 md:px-8">

        <PhotoMosaic
          photos={photos}
          onPhotoClick={(photo, photos) => {
            const photoIndex = photos.findIndex(p => p.id === photo.id);
            navigate(`/album/${albumId}/photo/${photoIndex}`, { state: { photos } });
          }}
          onDeletePhoto={handleDeletePhoto}
          onSetCover={handleSetCover}
          currentCover={albumData?.coverPhoto}
          selectedPhotos={selectedPhotos}
          onToggleSelection={toggleSelection}
        />
      </div>

      <EditAlbumModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onUpdateAlbum={handleUpdateAlbum}
        albumData={albumData}
      />

      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        expectedPin={uploadPin}
        onPinValidated={handlePinValidated}
        title="Enter Upload PIN"
        description="Enter 6-digit PIN to upload photos"
      />
    </div>
  );
};

AlbumPage.refreshPhotos = (albumPageRef) => {
  if (albumPageRef.current) {
    albumPageRef.current.refreshPhotos();
  }
};

export default AlbumPage;